import {
  FormEvent,
  forwardRef,
  ForwardRefRenderFunction,
  InputHTMLAttributes,
  useCallback,
} from "react";
import { FieldError } from "react-hook-form";
import { cep, cpf, currency, date } from "./masks";

import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input as FormInput,
} from "./styles";

type InputType =
  | "text"
  | "email"
  | "password"
  | "number"
  | "tel"
  | "url"
  | "date"
  | "time"
  | "datetime-local";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label?: string;
  error?: string | FieldError;
  showErrorMessage?: boolean;
  borderColor: string;
  textColor?: string;

  mask?: "cep" | "currency" | "cpf" | "phone" | "date";
  type?: InputType;
}

const InputBase: ForwardRefRenderFunction<HTMLInputElement, InputProps> = (
  { id, label, error, showErrorMessage = true, borderColor, textColor, mask = "", type = "text", ...rest },
  ref
) => {
  const handleKeyUp = useCallback(
    (e: FormEvent<HTMLInputElement>) => {
      if (mask === "cep") cep(e);
      if (mask === "currency") currency(e);
      if (mask === "cpf") cpf(e);
      if (mask === "date") date(e);
    },
    [mask]
  );

  return (
    <FormControl isInvalid={!!error}>
      {!!label && <FormLabel htmlFor={id} $textColor={textColor}>{label}</FormLabel>}

      {!mask ? (
        <FormInput
          id={id}
          ref={ref}
          type={type} 
          $bordercolor={borderColor}
          aria-invalid={!!error && showErrorMessage ? "true" : "false"}
          autoComplete={rest.autoComplete || "on"}
          {...rest}
        />
      ) : (
        <FormInput
          id={id}
          ref={ref}
          type={type} 
          $bordercolor={borderColor}
          aria-invalid={!!error && showErrorMessage ? "true" : "false"}
          onKeyUp={handleKeyUp}
          autoComplete={rest.autoComplete || "on"}
          {...rest}
        />
      )}

      {!!error && showErrorMessage && (
        <FormErrorMessage data-testid="error-message">
          {typeof error === 'string' ? error : error.message}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export const Input = forwardRef(InputBase);
